import { COVERAGE_REPORT_STATUS_LABEL, worstStatus, type CoverageReportRow, type CoverageReportStatus } from './coverageReport'
import { matchesEmployee } from './employeeSearch'

/**
 * The leave and availability report (docs/product/reports.md): leave either as
 * requested, read from the requests and their decisions, or as scheduled,
 * read from the published schedule. Pure, so the screen and the exports read
 * one result.
 */
export type LeaveSource = 'requested' | 'scheduled'
export type LeaveRequestState = 'pending' | 'approved' | 'returned' | 'denied'
export type LeaveDecision = 'approve' | 'approve_modified' | 'return' | 'deny'

export const LEAVE_STATE_LABEL: Record<LeaveRequestState, string> = {
  pending: 'Pending',
  approved: 'Approved',
  returned: 'Returned',
  denied: 'Denied',
}

export const LEAVE_DECISION_LABEL: Record<LeaveDecision, string> = {
  approve: 'Approved',
  approve_modified: 'Approved with modification',
  return: 'Returned',
  deny: 'Denied',
}

export type LeaveRecord = {
  id: string
  employeeId: string
  identifier: string
  employee: string
  teamId: string
  team: string
  // Every calendar date the leave covers, ISO, in order.
  dates: string[]
  leaveType: string
  // False for a partial-day request; absent for scheduled leave.
  fullDay?: boolean
  // Null for scheduled leave, which has no request behind it.
  state: LeaveRequestState | null
  decision: LeaveDecision | null
  decidedBy: string | null
  decidedAt: string | null
}

export type LeaveRow = LeaveRecord & {
  startDate: string
  endDate: string
  days: number
  supervisorId: string | null
  coverage: CoverageReportStatus
  coverageEffect: string
}

export type LeaveFilters = {
  state?: LeaveRequestState
  decision?: LeaveDecision
  leaveType?: string
  coverage?: CoverageReportStatus
  teamId?: string
  supervisorId?: string
  search?: string
}

// How the team's coverage reads on the leave's dates: the most urgent status
// among the team's rows on those dates, and how many dates it holds on.
export function coverageEffectFor(
  dates: string[],
  teamId: string,
  coverage: CoverageReportRow[],
): { status: CoverageReportStatus; label: string } {
  const dateSet = new Set(dates)
  const rows = coverage.filter((row) => row.teamId === teamId && dateSet.has(row.date) && row.assignment !== 'Whole shift')
  const status = worstStatus(rows.map((row) => row.status))
  if (status === 'understaffed' || status === 'missing-qualification' || status === 'at-risk') {
    const datesAtStatus = new Set(rows.filter((row) => row.status === status).map((row) => row.date)).size
    return { status, label: `${COVERAGE_REPORT_STATUS_LABEL[status]} on ${datesAtStatus} ${datesAtStatus === 1 ? 'date' : 'dates'}` }
  }
  return { status, label: COVERAGE_REPORT_STATUS_LABEL[status] }
}

export function buildLeaveRows(
  records: LeaveRecord[],
  range: { from: string; to: string },
  coverage: CoverageReportRow[],
  supervisors: Map<string, string>,
): LeaveRow[] {
  return records.flatMap((record) => {
    const dates = record.dates.filter((date) => date >= range.from && date <= range.to)
    if (dates.length === 0) return []
    const effect = coverageEffectFor(dates, record.teamId, coverage)
    return [
      {
        ...record,
        dates,
        startDate: dates[0],
        endDate: dates[dates.length - 1],
        days: dates.length,
        supervisorId: supervisors.get(record.teamId) ?? null,
        coverage: effect.status,
        coverageEffect: effect.label,
      },
    ]
  })
}

export function filterLeaveRows(rows: LeaveRow[], filters: LeaveFilters): LeaveRow[] {
  return rows.filter(
    (row) =>
      (!filters.state || row.state === filters.state) &&
      (!filters.decision || row.decision === filters.decision) &&
      (!filters.leaveType || row.leaveType === filters.leaveType) &&
      (!filters.coverage || row.coverage === filters.coverage) &&
      (!filters.teamId || row.teamId === filters.teamId) &&
      (!filters.supervisorId || row.supervisorId === filters.supervisorId) &&
      (!filters.search || matchesEmployee(filters.search, row.employee, row.identifier)),
  )
}

export function totalLeaveDays(rows: LeaveRow[]): number {
  return rows.reduce((sum, row) => sum + row.days, 0)
}

function nextDay(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number)
  const next = new Date(Date.UTC(year, month - 1, day + 1))
  return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-${String(next.getUTCDate()).padStart(2, '0')}`
}

// Scheduled leave is stored a day at a time; the report shows each unbroken
// run of the same leave as one row.
export function groupScheduledDays(
  days: { employeeId: string; identifier: string; employee: string; teamId: string; team: string; date: string; leaveType: string }[],
): LeaveRecord[] {
  const sorted = [...days].sort(
    (a, b) =>
      a.employeeId.localeCompare(b.employeeId) ||
      a.teamId.localeCompare(b.teamId) ||
      a.leaveType.localeCompare(b.leaveType) ||
      a.date.localeCompare(b.date),
  )
  const records: LeaveRecord[] = []
  for (const day of sorted) {
    const last = records[records.length - 1]
    if (
      last &&
      last.employeeId === day.employeeId &&
      last.teamId === day.teamId &&
      last.leaveType === day.leaveType &&
      nextDay(last.dates[last.dates.length - 1]) === day.date
    ) {
      last.dates.push(day.date)
      continue
    }
    records.push({
      id: `${day.employeeId}|${day.leaveType}|${day.date}`,
      employeeId: day.employeeId,
      identifier: day.identifier,
      employee: day.employee,
      teamId: day.teamId,
      team: day.team,
      dates: [day.date],
      leaveType: day.leaveType,
      state: null,
      decision: null,
      decidedBy: null,
      decidedAt: null,
    })
  }
  return records
}
